import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Globe, Sun, Moon, Laptop, LogOut } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../auth/AuthContext';
import { adminPageTitleKey } from './adminNav';
import { useAdminChrome } from './AdminChromeContext';
import type { Language, Theme } from '../../types';

/**
 * Compact Admin Control Center top bar — breadcrumbs, page title, lang/theme, account.
 */
export const AdminTopBar: React.FC = () => {
  const { language, setLanguage, direction, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const { crumbs, pageAction } = useAdminChrome();
  const [langOpen, setLangOpen] = React.useState(false);
  const [themeOpen, setThemeOpen] = React.useState(false);
  const Chevron = direction === 'rtl' ? ChevronLeft : ChevronRight;
  const titleKey = adminPageTitleKey(location.pathname);
  const isTopLevel = crumbs.length === 0;

  return (
    <header className="bc-cc-topbar sticky top-0 z-30 border-b border-line/80 dark:border-line-dark bg-paper/95 dark:bg-ink/95 backdrop-blur-md">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-50 focus:px-3 focus:py-2 focus:bg-brand-500 focus:text-white focus:rounded-md text-xs"
      >
        {t('a11y.skipToContent')}
      </a>
      <div className="h-12 px-3 sm:px-4 lg:px-5 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          {!isTopLevel && crumbs.length > 0 ? (
            <nav className="flex items-center gap-1.5 text-[11px] text-fog flex-wrap" aria-label="Breadcrumb">
              {crumbs.map((c, i) => (
                <React.Fragment key={`${c.label}-${i}`}>
                  {i > 0 && <Chevron className="w-3 h-3 shrink-0 opacity-60" />}
                  {c.href ? (
                    <Link
                      to={c.href}
                      className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors duration-150 truncate max-w-[10rem] sm:max-w-none"
                    >
                      {c.label}
                    </Link>
                  ) : (
                    <span className="font-semibold text-ink dark:text-paper truncate max-w-[14rem]">
                      {c.label}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          ) : (
            <h1 className="text-sm font-semibold text-ink dark:text-paper truncate">
              {t(titleKey)}
            </h1>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {pageAction}
          <div className="relative">
            <button
              type="button"
              className="bc-cc-icon-btn"
              aria-label={t('nav.language')}
              aria-expanded={langOpen}
              onClick={() => {
                setLangOpen((v) => !v);
                setThemeOpen(false);
              }}
            >
              <Globe className="w-3.5 h-3.5" />
              <span className="hidden sm:inline text-[10px] font-semibold uppercase">{language}</span>
            </button>
            {langOpen && (
              <div className="absolute end-0 mt-1 min-w-[7rem] rounded-md border border-line dark:border-line-dark bg-white dark:bg-ink-elevated shadow-[var(--bc-shadow-md)] py-1 z-50">
                {(['en', 'ar', 'ckb'] as Language[]).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    className={`block w-full text-start px-3 py-1.5 text-[11px] font-medium ${
                      language === lang
                        ? 'bg-brand-500/10 text-brand-700 dark:text-brand-300'
                        : 'text-ink dark:text-paper hover:bg-soft dark:hover:bg-ink-soft'
                    }`}
                    onClick={() => {
                      setLanguage(lang);
                      setLangOpen(false);
                    }}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <button
              type="button"
              className="bc-cc-icon-btn"
              aria-label={t('nav.theme')}
              aria-expanded={themeOpen}
              onClick={() => {
                setThemeOpen((v) => !v);
                setLangOpen(false);
              }}
            >
              {theme === 'dark' ? (
                <Moon className="w-3.5 h-3.5" />
              ) : theme === 'system' ? (
                <Laptop className="w-3.5 h-3.5" />
              ) : (
                <Sun className="w-3.5 h-3.5" />
              )}
            </button>
            {themeOpen && (
              <div className="absolute end-0 mt-1 min-w-[7rem] rounded-md border border-line dark:border-line-dark bg-white dark:bg-ink-elevated shadow-[var(--bc-shadow-md)] py-1 z-50">
                {(['light', 'dark', 'system'] as Theme[]).map((th) => (
                  <button
                    key={th}
                    type="button"
                    className={`block w-full text-start px-3 py-1.5 text-[11px] font-medium capitalize ${
                      theme === th
                        ? 'bg-brand-500/10 text-brand-700 dark:text-brand-300'
                        : 'text-ink dark:text-paper hover:bg-soft dark:hover:bg-ink-soft'
                    }`}
                    onClick={() => {
                      setTheme(th);
                      setThemeOpen(false);
                    }}
                  >
                    {th}
                  </button>
                ))}
              </div>
            )}
          </div>
          {user && (
            <span className="hidden lg:inline text-[10px] text-fog truncate max-w-[9rem]" dir="ltr">
              {user.email}
            </span>
          )}
          <button
            type="button"
            className="bc-cc-icon-btn"
            onClick={() => void logout()}
            aria-label={t('portal.signOut')}
            title={t('portal.signOut')}
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </header>
  );
};
